import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";

const RangeSlider = ({
  min = 0,
  max = 100,
  step = 1,
  initialMin = 20,
  initialMax = 80,
  onChange,
}) => {
  const trackRef = useRef(null);
  const [minVal, setMinVal] = useState(initialMin);
  const [maxVal, setMaxVal] = useState(initialMax);
  const [activeThumb, setActiveThumb] = useState(null);

  const getPercent = (value) => ((value - min) / (max - min)) * 100;

  const handleMove = (clientX) => {
    if (!trackRef.current || !activeThumb) return;

    const rect = trackRef.current.getBoundingClientRect();
    let percent = (clientX - rect.left) / rect.width;
    percent = Math.min(Math.max(percent, 0), 1);

    const value = Math.round((percent * (max - min) + min) / step) * step;

    // Track the value during drag but don't update until mouse up
    if (activeThumb === "min" && value < maxVal) {
      setMinVal(value);
    }

    if (activeThumb === "max" && value > minVal) {
      setMaxVal(value);
    }
  };

  const handleMouseUp = () => {
    // Update state only when the user releases the thumb
    if (activeThumb === "min") {
      onChange?.(minVal, maxVal);
    }
    if (activeThumb === "max") {
      onChange?.(minVal, maxVal);
    }
    setActiveThumb(null); // Stop the drag operation
  };

  useEffect(() => {
    const onMouseMove = (e) => handleMove(e.clientX);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [activeThumb, minVal, maxVal]);

  return (
    <div className="range-wrapper">
      <div className="range-track" ref={trackRef}>
        <div
          className="range-filled"
          style={{
            left: `${getPercent(minVal)}%`,
            width: `${getPercent(maxVal) - getPercent(minVal)}%`,
          }}
        />
        <div
          className="thumb left"
          style={{ left: `${getPercent(minVal)}%` }}
          onMouseDown={() => setActiveThumb("min")}
        />
        <div
          className="thumb right"
          style={{ left: `${getPercent(maxVal)}%` }}
          onMouseDown={() => setActiveThumb("max")}
        />
      </div>

      {/* Displaying the values */}
      <p className="range-value">
        <span>${minVal}</span>
        <span>${maxVal}</span>
      </p>
    </div>
  );
};

RangeSlider.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  initialMin: PropTypes.number,
  initialMax: PropTypes.number,
  onChange: PropTypes.func,
};

RangeSlider.defaultProps = {
  min: 0,
  max: 100,
  step: 1,
  initialMin: 20,
  initialMax: 80,
  onChange: () => {},
};

export default RangeSlider;
